'use client';

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import AutoScroll from 'embla-carousel-auto-scroll';
import { PageHomeSchema } from '@/sanity/pages/PageHomeTemplate';
import { GroqImage } from '@/helpers/Image';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';

const ease = [0.25, 0.1, 0.25, 1] as const;

type HeroQuote = NonNullable<PageHomeSchema['heroQuotes']>[number];

function QuoteCard({ quote }: { quote: HeroQuote }) {
  return (
    <div className="flex items-start gap-[2rem] lg:gap-[3.375rem] max-w-[27rem] lg:max-w-[55rem]">
      {quote.image && (
        <div className="w-[5rem] h-[5rem] lg:w-[6.625rem] lg:h-[6.625rem] rounded-[0.25rem] overflow-hidden flex-shrink-0 bg-[#141b17]">
          <GroqImage
            src={quote.image}
            alt={quote.name || ''}
            className="!w-full !h-full object-cover"
          />
        </div>
      )}
      <div className="flex flex-col gap-[1rem] lg:gap-[3.25rem] mt-[0.125rem] lg:mt-[0.5rem]">
        <p className="mb-0 font-geist font-semibold text-[1.625rem] lg:text-[1.75rem] leading-[1.33] text-black tracking-[-0.04875rem] lg:tracking-[-0.0525rem] w-[19.375rem] lg:w-auto lg:max-w-[36rem]">
          {quote.quote}
        </p>
        <span className="font-geist-mono font-semibold text-[1.375rem] lg:text-[1.625rem] leading-[1.33] text-[#003828] uppercase">
          {quote.name}
        </span>
      </div>
    </div>
  );
}

export default function HeroQuotesCarousel({ data }: { data: PageHomeSchema }) {
  const autoScroll = useMemo(
    () => AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false }),
    [],
  );

  const quotes = data?.heroQuotes;
  if (!quotes?.length) return null;

  const repeated = [...quotes, ...quotes, ...quotes];

  return (
    <section className="relative w-full bg-white pb-[2rem] lg:pb-[4rem]">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.9 }}
        className="w-full overflow-hidden"
        style={{
          maskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
        }}
      >
        <Carousel opts={{ loop: true }} plugins={[autoScroll]} className="w-full">
          <CarouselContent className="-ml-[3.5rem] lg:-ml-[10.625rem]">
            {repeated.map((quote, i) => (
              <CarouselItem
                key={`${quote._key ?? i}-${i}`}
                className="basis-auto pl-[3.5rem] lg:pl-[10.625rem]"
              >
                <div className="flex items-center gap-[3.5rem] lg:gap-[10.625rem]">
                  <QuoteCard quote={quote} />
                  <div className="w-px h-[7rem] lg:h-[6.5rem] bg-black/20 flex-shrink-0" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </motion.div>

      <div className="absolute left-1/2 -translate-x-1/2 h-[0.75rem] w-[128.5rem] z-20 lg:block hidden bottom-0">
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, ease, delay: 0.6 }}
          className="w-full h-full bg-[#3b6162]"
          style={{ originX: 0.5 }}
        />
      </div>
    </section>
  );
}
